/**
 * SeismosScorecard.tsx
 *
 * Unified scorecard for Seismos perf-efficiency data.
 * Supports three granularity levels driven by the `level` + `entityId` props.
 *
 * Usage:
 *   <SeismosScorecard level="stage" entityId={stageId}  apiBase={...} token={...} />
 *   <SeismosScorecard level="well"  entityId={wellId}   apiBase={...} token={...} />
 *   <SeismosScorecard level="pad"   entityId={padId}    apiBase={...} token={...} />
 *
 * level=stage  → x-axis: event_time (ms), lines per raw event
 * level=well   → x-axis: stage_number, points = avg metrics per stage, one well
 * level=pad    → x-axis: stage sequence, points = avg metrics per stage, color-coded by well
 *
 * view="rates" switches to the Rate Drop scorecard for the same level/entity:
 *   <SeismosScorecard view="rates" level="pad" entityId={padId} apiBase={...} token={...} />
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SeismosEventLog          } from './SeismosEventLog.tsx';
import { SeismosComparisonChart    } from './SeismosComparisonChart.tsx';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// ─── Brand ────────────────────────────────────────────────────────────────────
const GOLD        = '#F9C430';
const BG          = '#181716';
const CARD_BG     = '#242220';
const CARD_BG2    = '#2c2a28';
const BORDER      = '#38352f';
const TEXT_PRI    = '#EDE8DF';
const TEXT_SEC    = '#8a8480';
const TEXT_DIM    = '#504d48';

const C_EFF       = '#F9C430';
const C_PERF_FR   = '#e05c5c';
const C_PIPE_FR   = '#5b9bd5';
const C_EHD       = '#7ecb8f';
const C_REGRESS   = '#c8a020';
const C_SEISMOS   = '#a78bfa';   // purple — Seismos manual interventions

// Multi-well color palette (pad level)
const WELL_COLORS = ['#F9C430','#5b9bd5','#7ecb8f','#e05c5c','#c678dd','#56b6c2','#e5c07b','#98c379'];

// ─── Types ─────────────────────────────────────────────────────────────────────
export type ScorecardLevel = 'stage' | 'well' | 'pad';

interface ScorecardContext {
  pad_id:            number;
  pad_name:          string;
  well_id?:          number;
  well_name?:        string;
  stage_id?:         number;
  stage_number?:     number;
  start_ts?:         number | null;
  end_ts?:           number | null;
  uniformity_index?: number | null;
}

interface SeismosEvent {
  id:                           number;
  eff:                          number | null;
  perf_frict:                   number | null;
  pipe_frict:                   number | null;
  ehd:                          number | null;
  intra_stage_uniformity_index: number | null;
  initial_rate:                 number | null;
  rate_change:                  number | null;
  event_time:                   string;
  event_time_ms:                number;
  event_received_time:          number | null;  // epoch ms — when we received it from SAFA
  processing_status:            string | null;
  issues:                       string | null;  // JSON string or null
  annotations:                  string | null;  // JSON array string or null
  // present on bulk /seismos/events responses
  stage_id?:                    number;
  stage_number?:                number;
  well_id?:                     number;
  well_name?:                   string;
}

interface StageAggregate {
  stage_id:                number;
  stage_number:            number;
  start_ts:                number | null;
  end_ts:                  number | null;
  well_id:                 number;
  well_name:               string;
  avg_eff:                 number | null;
  avg_perf_frict:          number | null;
  avg_pipe_frict:          number | null;
  avg_ehd:                 number | null;
  avg_intra_ui:            number | null;
  avg_initial_rate:        number | null;
  avg_rate_change:         number | null;
  metric_count:            number;
  avg_processing_time_ms:  number | null;
  uniformity_index:        number | null;
}

interface ScorecardResponse {
  level:   ScorecardLevel;
  context: ScorecardContext;
  events:  SeismosEvent[]   | null;
  stages:  StageAggregate[] | null;
}

interface MetricToggle {
  eff:        boolean;
  perf_frict: boolean;
  pipe_frict: boolean;
  ehd:        boolean;
  regression: boolean;
}

export type ScorecardView = 'events' | 'rates';

interface Props {
  level:     ScorecardLevel;
  entityId:  number;
  apiBase:   string;
  token:     string;
  isEditor?: boolean;
  view?:     ScorecardView;   // 'events' (default) = perf-eff scorecard, 'rates' = rate drop scorecard
  padId?:    number;          // required for view='rates' (data is fetched pad-wide)
  padName?:  string;
}

// ─── Utilities ────────────────────────────────────────────────────────────────
const fmtTime     = (ms: number) => new Date(ms).toISOString().slice(11, 19);
const fmtDateTime = (ms: number) => new Date(ms).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';

/** Format epoch-ms latency as "Xs" or "Xm Ys" */
function fmtLatency(ms: number | null): string {
  if (ms == null || ms < 0) return '—';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

/** Compute per-event latency ms from event_time_ms + event_received_time */
function eventLatencyMs(ev: { event_time_ms: number; event_received_time: number | null }): number | null {
  if (ev.event_received_time == null || !ev.event_time_ms) return null;
  const diff = ev.event_received_time - ev.event_time_ms;
  return diff >= 0 ? diff : null;
}

function latencyColor(ms: number | null): string {
  if (ms == null) return TEXT_SEC;
  const sec = ms / 1000;
  return sec <= 60 ? '#7ecb8f' : sec <= 300 ? '#e0b84a' : '#e05c5c';
}

function s(v: number | null, dec = 1, unit = ''): string {
  return v != null ? `${v.toFixed(dec)}${unit}` : '—';
}

function effColor(v: number | null): string {
  if (v == null) return TEXT_SEC;
  return v >= 70 ? '#7ecb8f' : v >= 45 ? '#e0b84a' : '#e05c5c';
}

function linearRegression(pts: { x: number; y: number }[]): { slope: number; intercept: number } | null {
  const n = pts.length;
  if (n < 2) return null;
  const sx  = pts.reduce((a, p) => a + p.x, 0);
  const sy  = pts.reduce((a, p) => a + p.y, 0);
  const sxy = pts.reduce((a, p) => a + p.x * p.y, 0);
  const sx2 = pts.reduce((a, p) => a + p.x * p.x, 0);
  const d   = n * sx2 - sx * sx;
  if (Math.abs(d) < 1e-10) return null;
  const slope     = (n * sxy - sx * sy) / d;
  const intercept = (sy - slope * sx) / n;
  return { slope, intercept };
}

// ─── Pill toggle ──────────────────────────────────────────────────────────────
const Pill: React.FC<{ label: string; color: string; active: boolean; onClick: () => void }> = ({
  label, color, active, onClick,
}) => (
  <button onClick={onClick} style={{
    display: 'flex', alignItems: 'center', gap: 5,
    background: active ? `${color}18` : 'transparent',
    border: `1px solid ${active ? color : BORDER}`,
    borderRadius: 20, padding: '3px 12px',
    color: active ? color : TEXT_DIM,
    fontSize: 11, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s', letterSpacing: '0.04em',
  }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? color : TEXT_DIM, display: 'inline-block' }} />
    {label}
  </button>
);



// ─── Header ───────────────────────────────────────────────────────────────────
const ScorecardHeader: React.FC<{
  level:   ScorecardLevel;
  context: ScorecardContext;
}> = ({ level, context }) => {

  const subtitle =
    level === 'stage'
      ? `${context.well_name ?? ''} — Stage ${context.stage_number}`
      : level === 'well'
      ? context.well_name ?? `Well ${context.well_id}`
      : context.pad_name;

  return (
    <div style={{
      borderBottom: `1px solid ${BORDER}`,
      paddingBottom: 14,
      marginBottom: 16,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 11, color: TEXT_SEC, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
        Seismos Scorecard
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: TEXT_PRI, letterSpacing: '-0.01em' }}>
        {subtitle}
      </div>
    </div>
  );
};

// ─── Shared centered card layout ─────────────────────────────────────────────
interface CardDef { label: string; value: string; color?: string; big?: boolean }
const StatCardLayout: React.FC<{ primary: CardDef[]; secondary: CardDef[] }> = ({ primary, secondary }) => (
  <>
    {/* Primary row — centered, fixed-width cards */}
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
      {primary.map(({ label, value, color, big }) => (
        <div key={label} style={{
          background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 6,
          padding: '8px 16px', minWidth: 110, textAlign: 'center',
        }}>
          <div style={{ fontSize: 10, color: TEXT_SEC, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
          <div style={{ fontSize: big ? 20 : 15, fontWeight: 700, color: color ?? TEXT_PRI, lineHeight: 1.1 }}>{value}</div>
        </div>
      ))}
    </div>
    {/* Secondary row — centered inline pills */}
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
      {secondary.map(({ label, value, color }) => (
        <div key={label} style={{
          background: 'transparent', border: `1px solid ${BORDER}`,
          borderRadius: 6, padding: '5px 12px', display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <span style={{ fontSize: 10, color: TEXT_DIM, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: color ?? TEXT_SEC }}>{value}</span>
        </div>
      ))}
    </div>
  </>
);

// ─── Summary stat cards ───────────────────────────────────────────────────────
const StatCards: React.FC<{
  level:          ScorecardLevel;
  events:         SeismosEvent[] | null;
  stages:         StageAggregate[] | null;
  slopeLabel:     { label: string; positive: boolean } | null;
  uniformityIndex?: number | null;
}> = ({ level, events, stages, slopeLabel, uniformityIndex }) => {
  const nums = useMemo(() => {
    if (level === 'stage' && events && events.length) {
      const effVals = events.map(e => e.eff).filter((v): v is number => v != null);
      const get = (k: keyof SeismosEvent) => events.map(e => e[k] as number | null).filter((v): v is number => v != null);
      const avg = (a: number[]) => a.length ? a.reduce((x, y) => x + y) / a.length : null;
      const startEff = effVals.length ? effVals[0]                  : null;
      const endEff   = effVals.length ? effVals[effVals.length - 1] : null;
      const latencies = events.map(e => eventLatencyMs(e)).filter((v): v is number => v != null);
      const avgLatency = latencies.length ? latencies.reduce((a, b) => a + b) / latencies.length : null;
      const successfulEvents = events.filter(e => e.processing_status === 'successful' || e.processing_status == null).length;
      return {
        avgEff: avg(get('eff')), startEff, endEff,
        avgPf:   avg(get('perf_frict')),
        avgPipef: avg(get('pipe_frict')),
        avgEhd:  avg(get('ehd')),
        avgLatency,
        totalEvents: events.length,
        successfulEvents,
      };
    }
    if (stages && stages.length) {
      const effVals = stages.map(s => s.avg_eff).filter((v): v is number => v != null);
      const get = (k: keyof StageAggregate) => stages.map(s => s[k] as number | null).filter((v): v is number => v != null);
      const avg = (a: number[]) => a.length ? a.reduce((x, y) => x + y) / a.length : null;
      const startEff = effVals.length ? effVals[0]                  : null;
      const endEff   = effVals.length ? effVals[effVals.length - 1] : null;
      return {
        avgEff: avg(get('avg_eff')), startEff, endEff,
        avgPf:   avg(get('avg_perf_frict')),
        avgPipef: avg(get('avg_pipe_frict')),
        avgEhd:  avg(get('avg_ehd')),
        avgLatency: null,
        totalEvents: null,
        successfulEvents: null,
      };
    }
    return null;
  }, [level, events, stages]);

  if (!nums) return null;

  if (level === 'stage') {
    const primaryCards = [
      { label: 'Avg Eff',    value: s(nums.avgEff,   1, '%'), color: effColor(nums.avgEff), big: true },
      { label: 'Start Eff',  value: s(nums.startEff, 1, '%'), color: effColor(nums.startEff) },
      { label: 'End Eff',    value: s(nums.endEff,   1, '%'), color: effColor(nums.endEff) },
      ...(uniformityIndex != null ? [{ label: 'Uniformity Index', value: uniformityIndex.toFixed(2), color: TEXT_PRI }] : []),
      ...(nums.avgLatency != null ? [{ label: 'Avg Processing Time', value: fmtLatency(nums.avgLatency), color: latencyColor(nums.avgLatency) }] : []),
      ...(nums.totalEvents != null ? [{
        label: 'Event Quality',
        value: `${nums.successfulEvents} / ${nums.totalEvents}`,
        color: nums.successfulEvents === nums.totalEvents ? '#7ecb8f' : '#e0b84a',
      }] : []),
    ];
    const secondaryCards = [
      { label: 'Avg Perf Fr.', value: s(nums.avgPf,    0, ' psi'), color: C_PERF_FR },
      { label: 'Avg Pipe Fr.', value: s(nums.avgPipef, 0, ' psi'), color: C_PIPE_FR },
      { label: 'Avg EHD',      value: s(nums.avgEhd,   3, ' in'),  color: C_EHD },
    ];
    return <StatCardLayout primary={primaryCards} secondary={secondaryCards} />;
  }

  const primaryCards = [
    { label: 'Avg Eff',   value: s(nums.avgEff,   1, '%'), color: effColor(nums.avgEff), big: true },
    { label: 'Start Eff', value: s(nums.startEff, 1, '%'), color: effColor(nums.startEff) },
    { label: 'End Eff',   value: s(nums.endEff,   1, '%'), color: effColor(nums.endEff) },
  ];
  const secondaryCards = [
    { label: 'Avg Perf Fr.', value: s(nums.avgPf,    0, ' psi'), color: C_PERF_FR },
    { label: 'Avg Pipe Fr.', value: s(nums.avgPipef, 0, ' psi'), color: C_PIPE_FR },
    { label: 'Avg EHD',      value: s(nums.avgEhd,   3, ' in'),  color: C_EHD },
  ];
  return <StatCardLayout primary={primaryCards} secondary={secondaryCards} />;
};

// ─── Shared Highcharts base config ────────────────────────────────────────────
const HC_BASE: Highcharts.Options = {
  credits: { enabled: false },
  title:   { text: undefined },
  chart: {
    backgroundColor: BG,
    style: { fontFamily: "'DM Mono','Fira Code','Courier New',monospace" },
    animation: false,
  },
  legend: {
    itemStyle:      { color: TEXT_SEC, fontSize: '11px', fontWeight: 'normal' },
    itemHoverStyle: { color: TEXT_PRI },
    backgroundColor: 'transparent',
  },
};

// ─── Chart — stage level (event_time_ms x-axis) ───────────────────────────────
const StageChart: React.FC<{
  events:          SeismosEvent[];
  metrics:         MetricToggle;
  regressionData:  { event_time_ms: number; regression: number }[] | null;
  slopeLabel:      { label: string; positive: boolean } | null;
}> = ({ events, metrics, regressionData, slopeLabel }) => {

  const options = useMemo<Highcharts.Options>(() => ({
    ...HC_BASE,
    chart: { ...HC_BASE.chart, height: 380 },
    subtitle: metrics.regression && slopeLabel ? {
      text: `Trend: ${slopeLabel.label}`,
      style: { color: slopeLabel.positive ? '#7ecb8f' : C_REGRESS, fontSize: '11px', fontWeight: '600' },
      align: 'center',
    } : { text: undefined },

    xAxis: {
      type: 'datetime',
      lineColor:    BORDER,
      tickColor:    BORDER,
      gridLineWidth: 0,
      labels: {
        style:     { color: TEXT_SEC, fontSize: '10px' },
        formatter: function () { return fmtTime(this.value as number); },
      },
      title: { text: 'Event Time (UTC)', style: { color: TEXT_SEC, fontSize: '11px' } },
    },

    yAxis: [
      { // 0 — eff (left)
        title:  { text: 'Perf Eff (%)', style: { color: TEXT_SEC, fontSize: '11px' } },
        labels: { format: '{value}%', style: { color: TEXT_SEC, fontSize: '10px' } },
        min: 0, max: 105,
        gridLineColor:     BORDER,
        gridLineDashStyle: 'Dot' as const,
        lineWidth: 0,
      },
      { // 1 — friction (right)
        title:  { text: 'psi', style: { color: TEXT_SEC, fontSize: '11px' } },
        labels: { style: { color: TEXT_SEC, fontSize: '10px' } },
        opposite: true,
        gridLineWidth: 0,
        lineWidth: 0,
      },
      { // 2 — EHD (right, offset)
        title:  { text: 'in', style: { color: C_EHD, fontSize: '11px' } },
        labels: { format: '{value:.3f}', style: { color: C_EHD, fontSize: '10px' } },
        opposite: true,
        gridLineWidth: 0,
        lineWidth: 0,
        offset: 55,
      },
    ],

    tooltip: {
      backgroundColor: CARD_BG2,
      borderColor:     BORDER,
      borderRadius:    6,
      useHTML:         true,
      style:           { color: TEXT_PRI, fontSize: '11px' },
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const unit = this.series.name === 'EHD' ? ' in'
                   : this.series.yAxis.opposite       ? ' psi'
                   : '%';
        return `<span style="color:${TEXT_SEC};font-size:10px">${fmtTime(this.x as number)} UTC</span><br/>
                <span style="color:${this.color as string}">●</span> <b>${this.series.name}:</b> ${(this.y as number).toFixed(1)}${unit}`;
      },
    },

    series: [
      // Perf Eff
      {
        type:    'line',
        name:    'Perf Eff',
        yAxis:   0,
        visible: metrics.eff,
        data:    events.filter(e => e.eff != null).map(e => [e.event_time_ms, e.eff!]),
        color:   C_EFF,
        lineWidth: 2.5,
        marker:  { radius: 3, symbol: 'circle' },
        connectNulls: true,
        zIndex:  5,
      },
      // Trend line (regression) — slope label shown as chart subtitle
      {
        type:    'line',
        name:    'Trend',
        yAxis:   0,
        visible: metrics.regression && regressionData != null,
        data:    regressionData?.map(d => [d.event_time_ms, d.regression]) ?? [],
        color:   C_REGRESS,
        lineWidth: 1.5,
        dashStyle: 'Dash',
        marker:  { enabled: false },
        enableMouseTracking: true,
        tooltip: { headerFormat: '', pointFormat: '<span style="color:{series.color}">Trend:</span> <b>{point.y:.1f}%</b>' },
        zIndex:  4,
      },
      // Perf friction
      {
        type:    'line',
        name:    'Perf Frict',
        yAxis:   1,
        visible: metrics.perf_frict,
        data:    events.filter(e => e.perf_frict != null).map(e => [e.event_time_ms, e.perf_frict!]),
        color:   C_PERF_FR,
        lineWidth: 1.5,
        marker:  { enabled: false },
        connectNulls: true,
      },
      // Pipe friction
      {
        type:    'line',
        name:    'Pipe Frict',
        yAxis:   1,
        visible: metrics.pipe_frict,
        data:    events.filter(e => e.pipe_frict != null).map(e => [e.event_time_ms, e.pipe_frict!]),
        color:   C_PIPE_FR,
        lineWidth: 1.5,
        marker:  { enabled: false },
        connectNulls: true,
      },
      // EHD
      {
        type:    'line',
        name:    'EHD',
        yAxis:   2,
        visible: metrics.ehd,
        data:    events.filter(e => e.ehd != null).map(e => [e.event_time_ms, e.ehd!]),
        color:   C_EHD,
        lineWidth: 1.5,
        marker:  { enabled: false },
        connectNulls: true,
      },
    ] as Highcharts.SeriesOptionsType[],
  }), [events, metrics, regressionData, slopeLabel]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

// ─── Chart — well / pad level (seqIndex x-axis) ───────────────────────────────
const AggregateChart: React.FC<{
  level:           'well' | 'pad';
  stages:          StageAggregate[];
  metrics:         MetricToggle;
  regressionData:  { seqIndex: number; regression: number }[] | null;
  slopeLabel:      { label: string; positive: boolean } | null;
}> = ({ level, stages, metrics, regressionData, slopeLabel }) => {

  const wellIds      = useMemo(() => [...new Set(stages.map(s => s.well_id))], [stages]);
  const wellColorMap = useMemo<Record<number, string>>(
    () => Object.fromEntries(wellIds.map((id, i) => [id, WELL_COLORS[i % WELL_COLORS.length]])),
    [wellIds],
  );
  const chartStages = useMemo(() => stages.map((s, i) => ({ ...s, seqIndex: i + 1 })), [stages]);
  const showWell = level === 'pad';

  const options = useMemo<Highcharts.Options>(() => ({
    ...HC_BASE,
    chart: { ...HC_BASE.chart, height: 380 },
    subtitle: metrics.regression && slopeLabel ? {
      text: `Trend: ${slopeLabel.label}`,
      style: { color: slopeLabel.positive ? '#7ecb8f' : C_REGRESS, fontSize: '11px', fontWeight: '600' },
      align: 'center',
    } : { text: undefined },

    xAxis: {
      min: 0.5, max: chartStages.length + 0.5,
      tickInterval: 1,
      lineColor: BORDER, tickColor: BORDER, gridLineWidth: 0,
      labels: {
        style:     { color: TEXT_SEC, fontSize: '10px' },
        formatter: function () {
          const st = chartStages.find(s => s.seqIndex === (this.value as number));
          return st ? `S${st.stage_number}` : '';
        },
      },
      title: {
        text:  level === 'well' ? 'Stage' : 'Stage Sequence (all wells)',
        style: { color: TEXT_SEC, fontSize: '11px' },
      },
    },

    yAxis: [
      { title: { text: 'Avg Perf Eff (%)', style: { color: TEXT_SEC, fontSize: '11px' } },
        labels: { format: '{value}%', style: { color: TEXT_SEC, fontSize: '10px' } },
        min: 0, max: 105, gridLineColor: BORDER, gridLineDashStyle: 'Dot' as const, lineWidth: 0 },
      { title: { text: 'psi', style: { color: TEXT_SEC, fontSize: '11px' } },
        labels: { style: { color: TEXT_SEC, fontSize: '10px' } },
        opposite: true, gridLineWidth: 0, lineWidth: 0 },
      { title: { text: 'in', style: { color: C_EHD, fontSize: '11px' } },
        labels: { format: '{value:.3f}', style: { color: C_EHD, fontSize: '10px' } },
        opposite: true, gridLineWidth: 0, lineWidth: 0, offset: 55 },
    ],

    tooltip: {
      backgroundColor: CARD_BG2, borderColor: BORDER, borderRadius: 6,
      shared: true, useHTML: true, style: { color: TEXT_PRI, fontSize: '11px' },
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const pts = this.points ?? [];
        const stage = chartStages.find(s => s.seqIndex === (this.x as number));
        let html = `<span style="color:${TEXT_SEC};font-size:10px">`;
        if (stage) html += (showWell ? `${stage.well_name} · ` : '') + `Stage ${stage.stage_number}`;
        html += `</span><br/>`;
        pts.forEach(p => {
          if (p.y == null) return;
          const unit = p.series.name.includes('EHD') ? ' in'
                     : (p.series as any).yAxis?.opposite ? ' psi' : '%';
          html += `<span style="color:${p.color as string}">●</span> <b>${p.series.name}:</b> ${(p.y as number).toFixed(1)}${unit}<br/>`;
        });
        return html;
      },
    },

    series: [
      // Pad: one scatter series per well
      ...(level === 'pad'
        ? wellIds.map(wid => ({
            type: 'scatter' as const,
            name: chartStages.find(s => s.well_id === wid)?.well_name ?? `Well ${wid}`,
            yAxis: 0, visible: metrics.eff,
            data: chartStages.filter(s => s.well_id === wid && s.avg_eff != null)
              .map(s => ({ x: s.seqIndex, y: s.avg_eff! })),
            color: wellColorMap[wid],
            marker: { radius: 5, symbol: 'circle' as const }, zIndex: 5,
          }))
        : []),

      // Well: single eff line
      ...(level === 'well' ? [{
        type: 'line' as const, name: 'Avg Perf Eff', yAxis: 0, visible: metrics.eff,
        data: chartStages.map(s => ({ x: s.seqIndex, y: s.avg_eff })),
        color: C_EFF, lineWidth: 2.5, connectNulls: true, zIndex: 5,
        marker: { radius: 4, symbol: 'circle' as const },
      }] : []),

      // Trend
      { type: 'line', name: 'Trend', yAxis: 0,
        visible: metrics.regression && regressionData != null,
        data: regressionData?.map(d => [d.seqIndex, d.regression]) ?? [],
        color: C_REGRESS, lineWidth: 1.5, dashStyle: 'Dash',
        marker: { enabled: false },
        enableMouseTracking: true,
        tooltip: { headerFormat: '', pointFormat: '<span style="color:{series.color}">Trend:</span> <b>{point.y:.1f}%</b>' },
        zIndex: 4 },

      { type: 'line', name: 'Avg Perf Frict', yAxis: 1, visible: metrics.perf_frict,
        data: chartStages.filter(s => s.avg_perf_frict != null).map(s => [s.seqIndex, s.avg_perf_frict!]),
        color: C_PERF_FR, lineWidth: 1.5, marker: { enabled: false }, connectNulls: true },

      { type: 'line', name: 'Avg Pipe Frict', yAxis: 1, visible: metrics.pipe_frict,
        data: chartStages.filter(s => s.avg_pipe_frict != null).map(s => [s.seqIndex, s.avg_pipe_frict!]),
        color: C_PIPE_FR, lineWidth: 1.5, marker: { enabled: false }, connectNulls: true },

      { type: 'line', name: 'Avg EHD', yAxis: 2, visible: metrics.ehd,
        data: chartStages.filter(s => s.avg_ehd != null).map(s => [s.seqIndex, s.avg_ehd!]),
        color: C_EHD, lineWidth: 1.5, marker: { enabled: false }, connectNulls: true },

    ] as Highcharts.SeriesOptionsType[],
  }), [chartStages, wellIds, wellColorMap, metrics, regressionData, level, showWell, slopeLabel]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

// ─── Stage aggregate table (well / pad levels) with expandable event rows ─────
const StageTable: React.FC<{
  stages:   StageAggregate[];
  showWell: boolean;
  apiBase:  string;
  token:    string;
}> = ({ stages, showWell, apiBase, token }) => {
  const [expanded,    setExpanded]    = useState<number | null>(null); // stage_id
  const [stageEvents, setStageEvents] = useState<Record<number, SeismosEvent[]>>({});
  const [loadingId,   setLoadingId]   = useState<number | null>(null);

  const toggleStage = async (stage_id: number) => {
    if (expanded === stage_id) { setExpanded(null); return; }
    setExpanded(stage_id);
    if (stageEvents[stage_id]) return; // already loaded
    setLoadingId(stage_id);
    try {
      const res = await fetch(`${apiBase}/seismos/scorecard/stage/${stage_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        // eff is 0-1 in DB — normalize × 100
        const evs: SeismosEvent[] = (json.events ?? []).map((e: SeismosEvent) => ({
          ...e,
          eff: e.eff != null ? Math.round(e.eff * 1000) / 10 : null,
        }));
        setStageEvents(prev => ({ ...prev, [stage_id]: evs }));
      }
    } catch { /* silently ignore */ }
    setLoadingId(null);
  };

  const colCount = (showWell ? 1 : 0) + 10; // Well?, Stage, Avg Eff, Perf Fr, Pipe Fr, EHD, UI, Avg Proc Time, Events, Int.

  return (
    <div style={{ marginTop: 20 }}>
      <div style={sectionLabel}>Stage Summary</div>
      <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ position: 'sticky', top: 0, background: CARD_BG, zIndex: 1 }}>
            <tr>
              {[...(showWell ? ['Well'] : []), 'Stage', 'Avg Eff %', 'Avg Perf Fr.', 'Avg Pipe Fr.', 'Avg EHD', 'UI', 'Avg Proc. Time', 'Events'].map(h => (
                <th key={h} style={{ ...thStyle, textAlign: h === 'Well' ? 'left' : 'center' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stages.map(st => (
              <React.Fragment key={st.stage_id}>
                <tr
                  style={{ borderBottom: `1px solid ${BORDER}20`, cursor: 'pointer' }}
                  onClick={() => toggleStage(st.stage_id)}
                  onMouseEnter={e => (e.currentTarget.style.background = `${GOLD}08`)}
                  onMouseLeave={e => (e.currentTarget.style.background = expanded === st.stage_id ? `${GOLD}06` : 'transparent')}
                >
                  {showWell && (
                    <td style={{ ...tdStyle, color: GOLD, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={st.well_name}>{st.well_name}</td>
                  )}
                  <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: TEXT_DIM, fontSize: 10 }}>{expanded === st.stage_id ? '▼' : '▶'}</span>
                    S{st.stage_number}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: effColor(st.avg_eff), fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {s(st.avg_eff, 1, '%')}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: TEXT_SEC, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{s(st.avg_perf_frict, 0, ' psi')}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: TEXT_SEC, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{s(st.avg_pipe_frict, 0, ' psi')}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: C_EHD,   fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{s(st.avg_ehd, 3, ' in')}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: TEXT_PRI, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {st.uniformity_index != null ? st.uniformity_index.toFixed(2) : <span style={{ color: TEXT_DIM }}>—</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: latencyColor(st.avg_processing_time_ms), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {fmtLatency(st.avg_processing_time_ms)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: TEXT_DIM, whiteSpace: 'nowrap' }}>{st.metric_count}</td>
                </tr>

                {/* Expanded events sub-table */}
                {expanded === st.stage_id && (
                  <tr>
                    <td colSpan={colCount} style={{ padding: 0, background: CARD_BG2 }}>
                      {loadingId === st.stage_id ? (
                        <div style={{ padding: '12px 20px', color: TEXT_SEC, fontSize: 11 }}>Loading events…</div>
                      ) : (stageEvents[st.stage_id]?.length ?? 0) === 0 ? (
                        <div style={{ padding: '12px 20px', color: TEXT_DIM, fontSize: 11 }}>No events recorded for this stage.</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                              {['#', 'Time (UTC)', 'Perf Eff %', 'Perf Frict', 'Pipe Frict', 'EHD', 'Processing Time', 'Status'].map(h => (
                                <th key={h} style={{ ...thStyle, fontSize: 9, padding: '5px 10px', textAlign: h === '#' || h === 'Status' ? 'center' : 'right', background: CARD_BG2 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {stageEvents[st.stage_id].map((ev, i) => {
                              const latMs = eventLatencyMs(ev);
                              return (
                                <tr key={ev.id} style={{ borderBottom: `1px solid ${BORDER}15` }}>
                                  <td style={{ ...tdStyle, textAlign: 'center', color: TEXT_DIM, padding: '6px 10px' }}>{i + 1}</td>
                                  <td style={{ ...tdStyle, textAlign: 'right', color: TEXT_SEC, fontVariantNumeric: 'tabular-nums', padding: '6px 10px' }}>{fmtTime(ev.event_time_ms)}</td>
                                  <td style={{ ...tdStyle, textAlign: 'right', color: effColor(ev.eff), fontWeight: 700, fontVariantNumeric: 'tabular-nums', padding: '6px 10px' }}>{s(ev.eff, 1, '%')}</td>
                                  <td style={{ ...tdStyle, textAlign: 'right', color: TEXT_SEC, fontVariantNumeric: 'tabular-nums', padding: '6px 10px' }}>{s(ev.perf_frict, 0, ' psi')}</td>
                                  <td style={{ ...tdStyle, textAlign: 'right', color: TEXT_SEC, fontVariantNumeric: 'tabular-nums', padding: '6px 10px' }}>{s(ev.pipe_frict, 0, ' psi')}</td>
                                  <td style={{ ...tdStyle, textAlign: 'right', color: C_EHD, fontVariantNumeric: 'tabular-nums', padding: '6px 10px' }}>{s(ev.ehd, 3, ' in')}</td>
                                  <td style={{ ...tdStyle, textAlign: 'right', color: latencyColor(latMs), fontVariantNumeric: 'tabular-nums', padding: '6px 10px' }}>{fmtLatency(latMs)}</td>
                                  <td style={{ ...tdStyle, textAlign: 'center', padding: '6px 10px' }}>
                                    {ev.processing_status ? (
                                      <span style={{
                                        color: ev.processing_status === 'successful' ? '#7ecb8f' : '#e0b84a',
                                        fontWeight: 600, fontSize: 9,
                                        background: ev.processing_status === 'successful' ? '#7ecb8f18' : '#e0b84a18',
                                        border: `1px solid ${ev.processing_status === 'successful' ? '#7ecb8f55' : '#e0b84a55'}`,
                                        borderRadius: 3, padding: '1px 5px',
                                      }}>{ev.processing_status}</span>
                                    ) : <span style={{ color: TEXT_DIM }}>—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Export utilities ─────────────────────────────────────────────────────────

type Cell = string | number | null;

/** Round to a real number (not a string) so Excel can sort/chart it */
const num = (v: number | null | undefined, dec: number): Cell =>
  v != null ? Number(v.toFixed(dec)) : null;

const isoMs = (ms: number | null | undefined): Cell =>
  ms != null ? new Date(ms).toISOString() : null;

/** issues / annotations are JSON strings — flatten to readable text for a cell */
function flattenJson(raw: string | null | undefined): Cell {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (Array.isArray(v)) {
      return v.length ? v.map(x => (typeof x === 'string' ? x : JSON.stringify(x))).join('; ') : null;
    }
    return typeof v === 'string' ? v : JSON.stringify(v);
  } catch {
    return raw;
  }
}

interface RowCtx { well_name?: string; well_id?: number; stage_number?: number; stage_id?: number }

/** Full event schema — same columns at every level. ctx overrides per-row ids (stage level). */
function eventsToRows(evs: SeismosEvent[], ctx: RowCtx = {}): Cell[][] {
  const header = [
    'Well', 'Well ID', 'Stage', 'Stage ID', 'Event ID',
    'Event Time (UTC)', 'Received Time (UTC)', 'Processing Time', 'Processing Time (s)',
    'Perf Eff %', 'Perf Frict (psi)', 'Pipe Frict (psi)', 'EHD (in)',
    'Intra-Stage UI', 'Initial Rate', 'Rate Change',
    'Status', 'Issues', 'Annotations',
  ];
  const rows = evs.map(e => {
    const lat = eventLatencyMs(e);
    const sn  = ctx.stage_number ?? e.stage_number;
    return [
      ctx.well_name ?? e.well_name ?? null,
      ctx.well_id   ?? e.well_id   ?? null,
      sn != null ? `S${sn}` : null,
      ctx.stage_id  ?? e.stage_id  ?? null,
      e.id,
      isoMs(e.event_time_ms),
      isoMs(e.event_received_time),
      lat != null ? fmtLatency(lat) : null,
      lat != null ? Math.round(lat / 1000) : null,
      num(e.eff, 1),
      num(e.perf_frict, 0),
      num(e.pipe_frict, 0),
      num(e.ehd, 3),
      num(e.intra_stage_uniformity_index, 4),
      num(e.initial_rate, 2),
      num(e.rate_change, 2),
      e.processing_status ?? null,
      flattenJson(e.issues),
      flattenJson(e.annotations),
    ];
  });
  return [header, ...rows];
}

function stageSummaryToRows(stages: StageAggregate[]): Cell[][] {
  const header = [
    'Well', 'Well ID', 'Stage', 'Stage ID', 'Start (UTC)', 'End (UTC)',
    'Avg Eff %', 'Avg Perf Fr. (psi)', 'Avg Pipe Fr. (psi)', 'Avg EHD (in)',
    'Avg Intra-Stage UI', 'Avg Initial Rate', 'Avg Rate Change',
    'Uniformity Index', 'Avg Proc. Time', 'Avg Proc. Time (s)', 'Events',
  ];
  const rows = stages.map(st => [
    st.well_name,
    st.well_id,
    `S${st.stage_number}`,
    st.stage_id,
    isoMs(st.start_ts),
    isoMs(st.end_ts),
    num(st.avg_eff, 1),
    num(st.avg_perf_frict, 0),
    num(st.avg_pipe_frict, 0),
    num(st.avg_ehd, 3),
    num(st.avg_intra_ui, 4),
    num(st.avg_initial_rate, 2),
    num(st.avg_rate_change, 2),
    num(st.uniformity_index, 4),
    st.avg_processing_time_ms != null ? fmtLatency(st.avg_processing_time_ms) : null,
    st.avg_processing_time_ms != null ? Math.round(st.avg_processing_time_ms / 1000) : null,
    st.metric_count,
  ]);
  return [header, ...rows];
}

// ─── PDF export (shared) ──────────────────────────────────────────────────────
// html2canvas → jsPDF, auto-saves without a print dialog. Highcharts SVGs are
// pre-rendered to PNG first because html2canvas draws SVG incompletely.
// Elements marked data-pdf-hide are left out.
async function exportScorecardPdf(el: HTMLElement, fileBase: string): Promise<void> {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas' as any),
    import('jspdf'      as any),
  ]);

  // ── 1. Pre-render Highcharts SVGs to PNG images from the LIVE element
  //    html2canvas has incomplete SVG support — replacing SVGs with img elements
  //    before capture ensures charts render fully and correctly.
  const svgCaptures: { png: string; w: number; h: number }[] = [];
  for (const svg of Array.from(el.querySelectorAll<SVGSVGElement>('.highcharts-root'))) {
    const rect = svg.getBoundingClientRect();
    const w = rect.width  || svg.clientWidth  || 600;
    const h = rect.height || svg.clientHeight || 380;
    // Serialize → base64 data URL → draw to canvas → PNG
    const svgStr  = new XMLSerializer().serializeToString(svg);
    const dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgStr)))}`;
    const png = await new Promise<string>(res => {
      const img  = new Image();
      img.onload = () => {
        const c   = document.createElement('canvas');
        c.width   = w * 2; c.height = h * 2;
        c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
        res(c.toDataURL('image/png'));
      };
      img.onerror = () => res('');
      img.src = dataUrl;
    });
    svgCaptures.push({ png, w, h });
  }

  // ── 2. Clone off-screen and clear all scroll / sticky constraints
  const clone = el.cloneNode(true) as HTMLElement;
  clone.style.cssText = [
    'position:fixed', 'top:-99999px', 'left:0',
    `width:${el.offsetWidth}px`,
    'overflow:visible', 'max-height:none', 'height:auto',
  ].join(';');

  clone.querySelectorAll<HTMLElement>('*').forEach(child => {
    const tag = child.tagName.toLowerCase();
    const isTableEl = tag === 'td' || tag === 'th' || tag === 'tr' || tag === 'table';
    // Don't touch overflow on table cells — clears text-overflow:ellipsis clipping
    if (!isTableEl) {
      child.style.overflow  = 'visible';
      child.style.overflowY = 'visible';
      child.style.overflowX = 'visible';
      child.style.height    = 'auto';
    }
    child.style.maxHeight = 'none';
    if (child.style.position === 'sticky') {
      child.style.position = 'relative';
      child.style.top      = 'auto';
    }
  });

  // ── 3. Replace SVG charts in clone with the pre-rendered PNG img elements
  const cloneSvgs = Array.from(clone.querySelectorAll<SVGSVGElement>('.highcharts-root'));
  cloneSvgs.forEach((svg, i) => {
    if (!svgCaptures[i]?.png) return;
    const img   = document.createElement('img');
    img.src     = svgCaptures[i].png;
    img.style.cssText = `width:${svgCaptures[i].w}px;height:${svgCaptures[i].h}px;display:block;`;
    // Replace the outermost highcharts container div, not just the SVG
    const container = svg.closest<HTMLElement>('.highcharts-container') ?? svg;
    container.parentNode?.replaceChild(img, container);
  });

  // ── 4. Remove expand arrows and expanded sub-rows from tables (PDF-only cleanup)
  //    Expand arrows have a ▶/▼ text node inside a span — tag them via data attr
  clone.querySelectorAll<HTMLElement>('[data-pdf-hide]').forEach(el => el.remove());
  // Remove any <tr> that is an expanded events sub-row (has a single td with colSpan)
  clone.querySelectorAll<HTMLTableRowElement>('tr').forEach(tr => {
    const cells = tr.cells;
    if (cells.length === 1 && cells[0].colSpan > 2) tr.remove();
  });
  // Remove expand arrow span (the ▶/▼ indicator inside stage column)
  clone.querySelectorAll<HTMLElement>('span').forEach(span => {
    if (span.textContent === '▶' || span.textContent === '▼') span.remove();
  });

  document.body.appendChild(clone);
  await new Promise<void>(r => requestAnimationFrame(() => setTimeout(r, 250)));

  const fullWidth  = clone.offsetWidth;
  const fullHeight = clone.scrollHeight;

  const canvas = await html2canvas(clone, {
    backgroundColor: '#181716',
    useCORS:         true,
    scale:           2,
    logging:         false,
    width:           fullWidth,
    height:          fullHeight,
    windowWidth:     fullWidth,
    windowHeight:    fullHeight,
    scrollX:         0,
    scrollY:         0,
    x:               0,
    y:               0,
  });

  document.body.removeChild(clone);

  const imgData  = canvas.toDataURL('image/png');
  const pxWidth  = canvas.width  / 2;
  const pxHeight = canvas.height / 2;

  const pdf = new jsPDF({
    orientation: pxWidth > pxHeight ? 'landscape' : 'portrait',
    unit:        'px',
    format:      [pxWidth, pxHeight],
    hotfixes:    ['px_scaling'],
  });

  pdf.addImage(imgData, 'PNG', 0, 0, pxWidth, pxHeight);
  pdf.save(`${fileBase}.pdf`);
}

// ─── Export Buttons ───────────────────────────────────────────────────────────
const ExportButtons: React.FC<{
  level:          ScorecardLevel;
  entityId:       number;
  context:        ScorecardContext;
  events:         SeismosEvent[] | null;
  stages:         StageAggregate[] | null;
  apiBase:        string;
  token:          string;
  scorecardRef:   React.RefObject<HTMLDivElement>;
}> = ({ level, entityId, context, events, stages, apiBase, token, scorecardRef }) => {
  const [exporting, setExporting] = useState(false);

  const fileBase =
    level === 'stage' ? `seismos_stage${context.stage_number}_${context.well_name ?? ''}` :
    level === 'well'  ? `seismos_well_${context.well_name ?? entityId}` :
    `seismos_pad_${context.pad_name ?? entityId}`;

  // ── Fetch all raw events in one call (well/pad) ───────────────────────────
  const fetchAllEvents = async (): Promise<SeismosEvent[]> => {
    const res = await fetch(`${apiBase}/seismos/events/${level}/${entityId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return (json.events ?? []).map((e: SeismosEvent) => ({
      ...e,
      eff: e.eff != null ? Math.round(e.eff * 1000) / 10 : null,
    }));
  };

  // ── Excel (two sheets for every level) ────────────────────────────────────
  const exportExcel = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx').catch(() => null);
      if (!XLSX) { alert('Run: npm install xlsx'); return; }

      const wb = XLSX.utils.book_new();

      if (level === 'stage' && events) {
        // Sheet 1: Stage info
        const infoRows: Cell[][] = [
          ['Pad',              context.pad_name ?? ''],
          ['Well',             context.well_name ?? ''],
          ['Stage',            `S${context.stage_number}`],
          ['Stage ID',         context.stage_id ?? null],
          ['Start (UTC)',      isoMs(context.start_ts)],
          ['End (UTC)',        isoMs(context.end_ts)],
          ['Uniformity Index', num(context.uniformity_index, 4)],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(infoRows), 'Stage Info');

        // Sheet 2: Events (full schema)
        const ws = XLSX.utils.aoa_to_sheet(eventsToRows(events, {
          well_name:    context.well_name,
          well_id:      context.well_id,
          stage_number: context.stage_number,
          stage_id:     context.stage_id,
        }));
        XLSX.utils.book_append_sheet(wb, ws, 'Events');
      } else if (stages) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(stageSummaryToRows(stages)), 'Stage Summary');

        const allEvs   = await fetchAllEvents();
        const eventsWs = XLSX.utils.aoa_to_sheet(
          allEvs.length ? eventsToRows(allEvs) : [['No event data']]
        );
        XLSX.utils.book_append_sheet(wb, eventsWs, 'All Events');
      }

      XLSX.writeFile(wb, `${fileBase}_export.xlsx`);
    } catch (e: any) {
      alert(`Excel export failed: ${e.message}`);
    } finally { setExporting(false); }
  };

  // ── PDF ───────────────────────────────────────────────────────────────────
  const exportPdf = async () => {
    const el = scorecardRef.current;
    if (!el) return;
    setExporting(true);
    try {
      await exportScorecardPdf(el, fileBase);
    } catch (e: any) {
      alert(`PDF export failed: ${e.message}\n\nMake sure html2canvas and jspdf are installed:\nnpm install html2canvas jspdf`);
    } finally { setExporting(false); }
  };

  const btnStyle = (color: string): React.CSSProperties => ({
    background: 'transparent', border: `1px solid ${color}`, color,
    borderRadius: 4, padding: '4px 12px', fontSize: 11, fontWeight: 600,
    cursor: exporting ? 'wait' : 'pointer', letterSpacing: '0.03em',
    opacity: exporting ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 4,
    transition: 'opacity 0.15s',
  });

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <button onClick={exportExcel} style={btnStyle(C_EHD)}     disabled={exporting} title="Export Excel (multi-sheet)">
        {exporting ? '…' : '⬇'} Excel
      </button>
      <button onClick={exportPdf}   style={btnStyle(C_PERF_FR)} disabled={exporting} title="Save as PDF">
        {exporting ? '…' : '⬇'} PDF
      </button>
    </div>
  );
};

// ─── Pad Refresh Button ───────────────────────────────────────────────────────
// Queues a SAFA refresh for the whole pad and shows progress.
//   POST /pads/<padId>/seismos-refresh   queue (returns the live request if one exists)
//   GET  /pads/<padId>/seismos-refresh   latest request, polled while queued/running
// The machina-workers seismos-refresh container does the actual work.

type RefreshStatus = 'queued' | 'running' | 'success' | 'error';

interface RefreshRequest {
  id:                 number;
  pad_id:             number;
  status:             RefreshStatus;
  requested_by_email: string | null;
  requested_at_ms:    number;
  started_at_ms:      number | null;
  finished_at_ms:     number | null;
  stages_checked:     number | null;
  events_inserted:    number | null;
  events_updated:     number | null;
  uniformity_updated: number | null;
  detail:             string | null;
  error:              string | null;
}

const REFRESH_POLL_MS = 4000;
const C_OK  = '#7ecb8f';
const C_BAD = '#e05c5c';

function ago(ms: number): string {
  const sec = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function elapsed(ms: number): string {
  const sec = Math.max(0, Math.round((Date.now() - ms) / 1000));
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

export const PadRefreshButton: React.FC<{
  padId:       number;
  apiBase:     string;
  token:       string;
  canRefresh:  boolean;      // false for Viewers — status shows, button disabled
  onComplete?: () => void;   // fired once when a refresh finishes successfully
  align?:      'flex-start' | 'center' | 'flex-end';
}> = ({ padId, apiBase, token, canRefresh, onComplete, align = 'flex-end' }) => {
  const [req,     setReq]     = useState<RefreshRequest | null>(null);
  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState<string | null>(null);
  const [, setTick]           = useState(0);   // re-render so the elapsed timer moves

  const prevStatus    = useRef<RefreshStatus | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const active = req?.status === 'queued' || req?.status === 'running';

  const fetchLatest = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/pads/${padId}/seismos-refresh`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setReq(await res.json());
    } catch { /* transient — next poll retries */ }
  }, [apiBase, padId, token]);

  // Load the latest request whenever the pad changes
  useEffect(() => {
    prevStatus.current = null;
    setReq(null);
    setPostErr(null);
    fetchLatest();
  }, [fetchLatest]);

  // Poll only while a refresh is in flight
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => { fetchLatest(); setTick(t => t + 1); }, REFRESH_POLL_MS);
    return () => clearInterval(id);
  }, [active, fetchLatest]);

  // Fire onComplete on the queued/running -> success transition only
  useEffect(() => {
    const now = req?.status ?? null;
    const was = prevStatus.current;
    if ((was === 'queued' || was === 'running') && now === 'success') onCompleteRef.current?.();
    prevStatus.current = now;
  }, [req?.status]);

  const start = async () => {
    setPosting(true);
    setPostErr(null);
    try {
      const res = await fetch(`${apiBase}/pads/${padId}/seismos-refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message ?? `HTTP ${res.status}`);
      setReq(body);
    } catch (e: any) {
      setPostErr(e.message);
    } finally {
      setPosting(false);
    }
  };

  let label = 'Refresh Pad Data';
  if (posting)                        label = 'Queuing…';
  else if (req?.status === 'queued')  label = 'Queued…';
  else if (req?.status === 'running') label = `Refreshing… ${elapsed(req.started_at_ms ?? req.requested_at_ms)}`;

  let statusText: string | null = null;
  let statusColor = TEXT_SEC;
  let statusTitle: string | undefined;

  if (postErr) {
    statusText = postErr; statusColor = C_BAD;
  } else if (req?.status === 'success' && req.finished_at_ms) {
    const changed = (req.events_inserted ?? 0) + (req.events_updated ?? 0);
    statusText = changed
      ? `Updated ${ago(req.finished_at_ms)} · ${req.events_inserted ?? 0} new · ${req.events_updated ?? 0} updated`
      : `Checked ${ago(req.finished_at_ms)} · no Seismos events found`;
    statusColor = changed ? C_OK : TEXT_SEC;
    statusTitle = req.detail ?? undefined;
  } else if (req?.status === 'error') {
    statusText  = `Last refresh failed${req.finished_at_ms ? ` ${ago(req.finished_at_ms)}` : ''}`;
    statusColor = C_BAD;
    statusTitle = req.error ?? undefined;
  }

  const disabled = !canRefresh || active || posting;

  return (
    <div data-pdf-hide style={{ display: 'flex', flexDirection: 'column', alignItems: align, gap: 3 }}>
      <button
        onClick={start}
        disabled={disabled}
        title={!canRefresh ? 'Editors and Managers only' : 'Pull the latest Seismos data for every stage on this pad'}
        style={{
          background:    active ? `${GOLD}18` : 'transparent',
          border:        `1px solid ${disabled && !active ? BORDER : GOLD}`,
          color:         disabled && !active ? TEXT_DIM : GOLD,
          borderRadius:  4,
          padding:       '4px 12px',
          fontSize:      11,
          fontWeight:    600,
          letterSpacing: '0.03em',
          cursor:        disabled ? (active ? 'progress' : 'not-allowed') : 'pointer',
          whiteSpace:    'nowrap',
          fontFamily:    'inherit',
        }}
      >
        ⟳ {label}
      </button>
      {statusText && (
        <span title={statusTitle} style={{ fontSize: 10, color: statusColor, whiteSpace: 'nowrap',
                                           cursor: statusTitle ? 'help' : 'default' }}>
          {statusText}
        </span>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// RATE DROPS VIEW  (view="rates")
// How many rate drops Machina commanded, why they weren't accepted, how many
// SAFA could analyze, and commanded vs SAFA-measured rate change.
// Data — two existing endpoints, both pad-wide:
//   GET /seismos/events/pad/<padId>                          SAFA-detected rate drops
//   GET /pads/<padId>/rule-executions?channel=rate_drop      commanded rate drops
// There is no FK between them, so in the browser:
//   • each firing is placed on the stage whose SAFA events span it, else the
//     nearest stage within 60 min (pad-wide, so neighbouring stages compete)
//   • each ACCEPTED firing is matched to the first unclaimed SAFA event ≤60 s later
// then everything is narrowed to the selected well / stage.
// ═════════════════════════════════════════════════════════════════════════════

const GREEN  = '#7ecb8f';
const RED    = '#e05c5c';
const ORANGE = '#e0a04a';
const PURPLE = '#a78bfa';
const BLUE   = '#5b9bd5';
const GRAY   = '#8a8480';

const NA = 'N/A';

// ─── Types ────────────────────────────────────────────────────────────────────
type FiringStatus = 'Accepted' | 'Rejected' | 'Timed out' | 'Failure';

interface RdEvent {
  id:                number;
  event_time_ms:     number;
  stage_id:          number;
  stage_number:      number;
  well_id:           number;
  well_name:         string;
  processable:       boolean;
  processing_status: string | null;
  reasons:           string[];
  initial_rate:      number | null;
  rate_change:       number | null;
  eff:               number | null;   // 0–1
}

interface RdFiring {
  id:               number;
  rule_name:        string;
  fired_at_ms:      number;
  rate_before:      number | null;
  rate_commanded:   number | null;
  commanded_drop:   number | null;
  status:           FiringStatus;
  reason:           string | null;
  reason_category:  'operator' | 'busy' | 'refused' | 'none' | null;
  mqtt_error:       string | null;
  stage_id:         number | null;
  stage_number:     number | null;
  well_id:          number | null;
  well_name:        string | null;
  matched_event_id: number | null;
}

// Raw API rows
interface ApiExecution {
  id: number; rule_id: number; rule_name: string; fired_at_ms: number; channel: string;
  from_value: number | null; to_value: number | null; mqtt_success: number; mqtt_error: string | null;
}

const MATCH_MS     = 60_000;        // accepted firing → SAFA event
const STAGE_PAD_MS = 60 * 60_000;   // firing outside every stage: nearest stage within 60 min

const STATUS_BY_CODE: Record<number, FiringStatus> = { 1: 'Accepted', 0: 'Rejected', 2: 'Timed out', 3: 'Failure' };

/** Petrix mqtt_error → short reason + category. Null for accepted drops. */
function petrixReason(status: FiringStatus, err: string | null): [string | null, RdFiring['reason_category']] {
  if (status === 'Accepted') return [null, null];
  const e = (err ?? '').toLowerCase();
  if (e.includes('cleared'))                         return ['Cleared by operator',           'operator'];
  if (e.includes('was rejected'))                    return ['Rejected by operator',          'operator'];
  if (e.includes('timed out') || status === 'Timed out') return ['Timed out',                 'operator'];
  if (e.includes('awaiting response'))               return ['Awaiting prior response',       'busy'];
  if (e.includes('in progress'))                     return ['Rate drop already in progress', 'busy'];
  if (e.includes('rate return'))                     return ['Waiting for rate return',       'busy'];
  if (e.includes('insufficient'))                    return ['Insufficient resources',        'refused'];
  if (e.includes('unstable rate'))                   return ['Unstable rate',                 'refused'];
  if (e.includes('unstable pressure'))               return ['Unstable pressure',             'refused'];
  if (e.includes('propilot'))                        return ['ProPilot not started',          'refused'];
  if (!e)                                            return ['No reason returned',            'none'];
  return [(err ?? '').slice(0, 60), 'none'];
}

function jsonList(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return (Array.isArray(v) ? v : [v]).map(x => String(x).trim()).filter(Boolean);
  } catch { return [raw]; }
}

/** Join commanded firings to SAFA events (both pad-wide). Events come back time-ordered. */
function joinRateDrops(rawEvents: SeismosEvent[], execs: ApiExecution[]): { events: RdEvent[]; firings: RdFiring[] } {
  const events: RdEvent[] = rawEvents
    .filter(e => e.event_time_ms != null && e.stage_id != null)
    .map(e => {
      const ok = e.processing_status === 'successful';
      return {
        id: e.id, event_time_ms: e.event_time_ms,
        stage_id: e.stage_id!, stage_number: e.stage_number!, well_id: e.well_id!, well_name: e.well_name ?? '',
        processable: ok, processing_status: e.processing_status,
        reasons: ok ? [] : (jsonList(e.annotations).length ? jsonList(e.annotations) : ['No reason given']),
        initial_rate: e.initial_rate, rate_change: e.rate_change, eff: e.eff,
      };
    })
    .sort((a, b) => a.event_time_ms - b.event_time_ms);

  // Stage windows from SAFA event times
  const win = new Map<number, { ev: RdEvent; min: number; max: number }>();
  events.forEach(e => {
    const w = win.get(e.stage_id);
    if (!w) win.set(e.stage_id, { ev: e, min: e.event_time_ms, max: e.event_time_ms });
    else { w.min = Math.min(w.min, e.event_time_ms); w.max = Math.max(w.max, e.event_time_ms); }
  });
  const windows = Array.from(win.values());
  const place = (t: number) => {
    let best: RdEvent | null = null, bestD = Infinity;
    for (const w of windows) {
      if (w.min <= t && t <= w.max) return w.ev;
      const d = Math.min(Math.abs(w.min - t), Math.abs(w.max - t));
      if (d < bestD) { bestD = d; best = w.ev; }
    }
    return bestD <= STAGE_PAD_MS ? best : null;
  };

  const claimed = new Set<number>();
  let cursor = 0;
  const firings: RdFiring[] = [...execs].sort((a, b) => a.fired_at_ms - b.fired_at_ms).map(x => {
    const status = STATUS_BY_CODE[x.mqtt_success] ?? 'Failure';
    const [reason, reason_category] = petrixReason(status, x.mqtt_error);
    const st = place(x.fired_at_ms);

    let matched: number | null = null;
    while (cursor < events.length && events[cursor].event_time_ms < x.fired_at_ms) cursor++;
    if (status === 'Accepted') {
      for (let i = cursor; i < events.length && events[i].event_time_ms - x.fired_at_ms <= MATCH_MS; i++) {
        if (!claimed.has(events[i].id)) { matched = events[i].id; claimed.add(matched); break; }
      }
    }
    const drop = x.from_value != null && x.to_value != null ? x.from_value - x.to_value : null;
    return {
      id: x.id, rule_name: x.rule_name, fired_at_ms: x.fired_at_ms,
      rate_before: x.from_value, rate_commanded: x.to_value,
      commanded_drop: drop != null ? Math.round(drop * 100) / 100 : null,
      status, reason, reason_category, mqtt_error: x.mqtt_error,
      stage_id: st?.stage_id ?? null, stage_number: st?.stage_number ?? null,
      well_id: st?.well_id ?? null, well_name: st?.well_name ?? null,
      matched_event_id: matched,
    };
  });
  return { events, firings };
}

// ─── Colors & labels ──────────────────────────────────────────────────────────
const STATUS_ORDER: FiringStatus[] = ['Accepted', 'Rejected', 'Timed out', 'Failure'];
const STATUS_COLOR: Record<FiringStatus, string> = {
  'Accepted': GREEN, 'Rejected': RED, 'Timed out': ORANGE, 'Failure': PURPLE,
};

const PETRIX_CATS = [
  { key: 'operator', name: 'Operator: cleared / rejected / timed out', color: RED },
  { key: 'busy',     name: 'Petrix busy with another drop',            color: ORANGE },
  { key: 'refused',  name: 'Petrix refused: rate / pressure / resources', color: PURPLE },
  { key: 'none',     name: 'No reason returned',                       color: GRAY },
];
const petrixColor = (cat: string | null) => PETRIX_CATS.find(c => c.key === cat)?.color ?? GRAY;

const SAFA_CATS = [
  { name: 'Pressure / rate not stable', color: RED,    test: (r: string) => /not stable|oscillation|rebound/i.test(r) },
  { name: 'During FR / proppant ramp',  color: ORANGE, test: (r: string) => /ramp/i.test(r) },
  { name: 'Rate-change size / speed',   color: BLUE,   test: (r: string) => /too large|too small|too slow|low rate/i.test(r) },
  { name: 'No rate drop (plug?)',       color: PURPLE, test: (r: string) => /no associated rate drop/i.test(r) },
  { name: 'Other',                      color: GRAY,   test: (_: string) => true },
];
const safaColor = (r: string) => SAFA_CATS.find(c => c.test(r))!.color;
const shortSafa = (r: string) => r.replace(' (possible plug issue)', ' (plug?)');

// ─── Formatting ───────────────────────────────────────────────────────────────
const pct   = (p: number | null) => (p == null ? NA : `${Math.round(p * 100)}%`);
const f1    = (x: number | null) => (x == null ? NA : x.toFixed(1));
const f2    = (x: number | null) => (x == null ? NA : x.toFixed(2));
const sgn   = (d: number | null) => (d == null ? NA : `${d > 0 ? '+' : ''}${d.toFixed(2)}`);
const effPc = (x: number | null) => (x == null ? NA : `${(x * 100).toFixed(1)}%`);
const fmtUtc = (ms: number) => new Date(ms).toISOString().replace('T', ' ').slice(5, 19);

const pctColor   = (p: number | null) => (p == null ? TEXT_DIM : p >= 0.6 ? GREEN : p >= 0.4 ? ORANGE : RED);
const deltaColor = (d: number | null) => (d == null ? TEXT_DIM : d <= -0.7 ? RED : d <= -0.4 ? ORANGE : GREEN);
const scoreColor = (s: number | null) => (s == null ? TEXT_DIM : s >= 60 ? GREEN : s >= 40 ? ORANGE : RED);

const avg = (a: number[]) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);

// ─── Aggregation ──────────────────────────────────────────────────────────────
interface Field { n: number; first: number; last: number; ok: number; tot: number; f: number; r: number; l: number; score: number }

/** Field metrics, per stage, from SAFA processing_status. Events must be time-ordered. */
function fieldOf(evs: RdEvent[]): Field | null {
  const byStage = new Map<number, boolean[]>();
  evs.forEach(e => {
    if (!byStage.has(e.stage_id)) byStage.set(e.stage_id, []);
    byStage.get(e.stage_id)!.push(e.processable);
  });
  if (!byStage.size) return null;
  const lists = Array.from(byStage.values());
  const n     = lists.length;
  const first = lists.filter(l => l[0]).length;
  const last  = lists.filter(l => l[l.length - 1]).length;
  const ok    = evs.filter(e => e.processable).length;
  const f = first / n, r = ok / evs.length, l = last / n;
  return { n, first, last, ok, tot: evs.length, f, r, l, score: 33.3 * (f + r + l) };
}

interface Agg {
  fld: Field | null;
  n: number; ok: number; bad: number; pct: number | null;
  fn: number; acc: number; nacc: number; apct: number | null;
  byStatus: Record<FiringStatus, number>;
  petrix: { name: string; count: number; color: string }[];
  safa:   { name: string; count: number; color: string }[];
  conf: number; cpct: number | null;
  pairs: number; cmd: number | null; act: number | null; delta: number | null;
  stages: number; noReason: number;
}

function aggregate(evs: RdEvent[], fs: RdFiring[], actualOf: (f: RdFiring) => number | null): Agg {
  const ok  = evs.filter(e => e.processable).length;
  const acc = fs.filter(f => f.status === 'Accepted');

  const byStatus = { 'Accepted': 0, 'Rejected': 0, 'Timed out': 0, 'Failure': 0 } as Record<FiringStatus, number>;
  fs.forEach(f => { byStatus[f.status]++; });

  const pMap = new Map<string, { count: number; color: string }>();
  fs.forEach(f => {
    if (!f.reason) return;
    const cur = pMap.get(f.reason) ?? { count: 0, color: petrixColor(f.reason_category) };
    cur.count++; pMap.set(f.reason, cur);
  });
  const sMap = new Map<string, number>();
  evs.forEach(e => { if (!e.processable) e.reasons.forEach(r => sMap.set(r, (sMap.get(r) ?? 0) + 1)); });

  const pairs = fs.filter(f => f.commanded_drop != null && actualOf(f) != null);
  return {
    fld: fieldOf(evs),
    n: evs.length, ok, bad: evs.length - ok, pct: evs.length ? ok / evs.length : null,
    fn: fs.length, acc: acc.length, nacc: fs.length - acc.length, apct: fs.length ? acc.length / fs.length : null,
    byStatus,
    petrix: Array.from(pMap, ([name, v]) => ({ name, ...v })).sort((a, b) => b.count - a.count),
    safa:   Array.from(sMap, ([name, count]) => ({ name: shortSafa(name), count, color: safaColor(name) })).sort((a, b) => b.count - a.count),
    conf: acc.filter(f => f.matched_event_id != null).length,
    cpct: acc.length ? acc.filter(f => f.matched_event_id != null).length / acc.length : null,
    pairs: pairs.length,
    cmd:   avg(pairs.map(f => f.commanded_drop!)),
    act:   avg(pairs.map(f => actualOf(f)!)),
    delta: avg(pairs.map(f => actualOf(f)! - f.commanded_drop!)),
    stages: new Set(evs.map(e => e.stage_id)).size,
    noReason: fs.filter(f => f.reason === 'No reason returned').length,
  };
}

// ─── Highcharts base ──────────────────────────────────────────────────────────
const RD_HC_BASE: Highcharts.Options = {
  credits: { enabled: false },
  title:   { text: undefined },
  chart:   { backgroundColor: BG, height: 240, animation: false,
             style: { fontFamily: "'DM Mono','Fira Code','Courier New',monospace" } },
  legend:  { itemStyle: { color: TEXT_SEC, fontSize: '11px', fontWeight: 'normal' },
             itemHoverStyle: { color: TEXT_PRI }, backgroundColor: 'transparent' },
  tooltip: { backgroundColor: CARD_BG2, borderColor: BORDER, borderRadius: 6,
             style: { color: TEXT_PRI, fontSize: '11px' }, shared: true },
  xAxis:   { lineColor: BORDER, tickColor: BORDER, labels: { style: { color: TEXT_SEC, fontSize: '10px' } } },
  yAxis:   { gridLineColor: BORDER, gridLineDashStyle: 'Dot', title: { text: undefined },
             labels: { style: { color: TEXT_SEC, fontSize: '10px' } } },
  plotOptions: { column: { borderWidth: 0, groupPadding: 0.1, pointPadding: 0.05 } },
};

const Chart: React.FC<{ options: Highcharts.Options }> = ({ options }) => (
  <HighchartsReact highcharts={Highcharts} options={Highcharts.merge(RD_HC_BASE, options)} />
);

// ─── Small pieces ─────────────────────────────────────────────────────────────
const SectionLabel: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
    <span style={{ fontSize: 11, color: TEXT_SEC, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{children}</span>
    {right && <span style={{ fontSize: 10, color: TEXT_DIM }}>{right}</span>}
  </div>
);

const Card: React.FC<{ label: string; value: string; color?: string; note?: string; small?: boolean }> = ({
  label, value, color, note, small,
}) => (
  <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 6, padding: '9px 10px', textAlign: 'center' }}>
    <div style={{ fontSize: 10, color: TEXT_SEC, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: small ? 13 : 18, fontWeight: 700, color: color ?? TEXT_PRI, lineHeight: 1.2 }}>{value}</div>
    {note && <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 3 }}>{note}</div>}
  </div>
);

const FieldCard: React.FC<{ label: string; value: string; color: string; detail: string; frac: number; weight: string; highlight?: boolean }> = ({
  label, value, color, detail, frac, weight, highlight,
}) => (
  <div style={{ background: CARD_BG, border: `1px solid ${highlight ? GOLD : BORDER}`, borderRadius: 6,
                padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
    <div style={{ fontSize: 10, color: TEXT_SEC, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ fontSize: 22, fontWeight: 700, color }}>{value}</span>
      <span style={{ fontSize: 10, color: TEXT_SEC, textAlign: 'right' }}>{detail}</span>
    </div>
    <div style={{ background: CARD_BG2, height: 6, borderRadius: 3 }}>
      <div style={{ background: color, height: 6, borderRadius: 3, width: `${Math.max(0, Math.min(1, frac)) * 100}%` }} />
    </div>
    <div style={{ fontSize: 10, color: TEXT_DIM }}>{weight}</div>
  </div>
);

const ReasonBars: React.FC<{
  title: string; sub: string; items: { name: string; count: number; color: string }[];
  total: number; legend: { name: string; color: string }[];
}> = ({ title, sub, items, total, legend }) => {
  const max = Math.max(1, ...items.map(i => i.count));
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 6, padding: '12px 14px',
                  display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <SectionLabel right={sub}>{title}</SectionLabel>
      {items.length === 0 && <div style={{ fontSize: 11, color: TEXT_DIM }}>None</div>}
      {items.map(r => (
        <div key={r.name} style={{ display: 'grid', gridTemplateColumns: '190px minmax(0,1fr) 78px', alignItems: 'center', gap: 8, fontSize: 11 }}>
          <span title={r.name} style={{ color: TEXT_PRI, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
          <div style={{ background: CARD_BG2, height: 10, borderRadius: 2 }}>
            <div style={{ background: r.color, height: 10, borderRadius: 2, width: `${(100 * r.count) / max}%` }} />
          </div>
          <span style={{ textAlign: 'right', color: TEXT_SEC }}>{r.count} · {pct(r.count / Math.max(total, 1))}</span>
        </div>
      ))}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 'auto', paddingTop: 6,
                    borderTop: `1px solid ${BORDER}`, fontSize: 10, color: TEXT_SEC }}>
        {legend.map(l => <span key={l.name}><span style={{ color: l.color }}>■</span> {l.name}</span>)}
      </div>
    </div>
  );
};

interface Col { h: string; align?: 'left' | 'right' }
interface CellV { t: string; c?: string }

const Table: React.FC<{ title: string; cols: Col[]; rows: CellV[][]; foot?: string }> = ({ title, cols, rows, foot }) => (
  <div style={{ marginTop: 16 }}>
    <SectionLabel>{title}</SectionLabel>
    <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead style={{ position: 'sticky', top: 0, background: CARD_BG, zIndex: 1 }}>
          <tr>{cols.map(c => <th key={c.h} style={{ ...rdThStyle, textAlign: c.align ?? 'right' }}>{c.h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} style={{ ...rdTdStyle, color: TEXT_DIM }}>No rate drops in this scope.</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${BORDER}40` }}>
              {r.map((c, j) => (
                <td key={j} title={c.t} style={{ ...rdTdStyle, color: c.c ?? TEXT_PRI, textAlign: cols[j]?.align ?? 'right',
                                                 maxWidth: cols[j]?.align === 'left' ? 280 : undefined,
                                                 overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.t}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {foot && <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 8 }}>{foot}</div>}
  </div>
);

// ─── Rate Drops view ───────────────────────────────────────────────────────────
const RateDropView: React.FC<Omit<Props, 'view'>> = ({ level, entityId, apiBase, token, padId, padName }) => {
  const [data,      setData]      = useState<{ events: RdEvent[]; firings: RdFiring[] } | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Both calls are pad-wide — placement and matching need the neighbouring
  // stages — and the selected well / stage is applied afterwards.
  useEffect(() => {
    if (!padId) { setError('padId is required for the Rate Drops view'); setLoading(false); return; }
    const headers = { Authorization: `Bearer ${token}` };
    const get = (url: string) => fetch(url, { headers }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); });
    setLoading(true);
    setError(null);
    Promise.all([
      get(`${apiBase}/seismos/events/pad/${padId}`),
      get(`${apiBase}/pads/${padId}/rule-executions?channel=rate_drop`),
    ])
      .then(([ev, ex]) => setData(joinRateDrops(ev.events ?? [], ex.items ?? [])))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [padId, apiBase, token]);

  const view = useMemo(() => {
    if (!data) return null;
    const inScope = <T extends { well_id: number | null; stage_id: number | null }>(x: T) =>
      level === 'pad' || (level === 'well' ? x.well_id === entityId : x.stage_id === entityId);
    const events  = data.events.filter(inScope);
    const firings = data.firings.filter(inScope);
    const ref0    = events[0] ?? firings.find(f => f.well_id != null);
    const context = {
      pad_name:     padName ?? 'Pad',
      well_id:      ref0?.well_id ?? undefined,
      well_name:    ref0?.well_name ?? undefined,
      stage_number: ref0?.stage_number ?? undefined,
    };
    const evById   = new Map(events.map(e => [e.id, e]));
    const actualOf = (f: RdFiring) => (f.matched_event_id != null ? evById.get(f.matched_event_id)?.rate_change ?? null : null);
    const agg      = (evs: RdEvent[], fs: RdFiring[]) => aggregate(evs, fs, actualOf);
    return { events, firings, context, evById, actualOf, agg, total: agg(events, firings) };
  }, [data, level, entityId, padName]);

  if (loading) return <div style={{ ...rootStyle, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT_SEC }}>Loading…</div>;
  if (error)   return <div style={{ ...rootStyle, color: RED, fontSize: 13 }}>Error: {error}</div>;
  if (!view)   return null;

  const { events, firings, context, evById, actualOf, agg, total: a } = view;
  const fd = a.fld;

  const title =
    level === 'stage' ? (context.stage_number != null ? `${context.well_name ?? ''} — Stage ${context.stage_number}` : 'Stage') :
    level === 'well'  ? context.well_name ?? 'Well' :
    context.pad_name;
  const times = events.map(e => e.event_time_ms).concat(firings.map(f => f.fired_at_ms));
  const sub = times.length
    ? `${a.stages} stage${a.stages === 1 ? '' : 's'} · ${fmtUtc(Math.min(...times))} – ${fmtUtc(Math.max(...times))} UTC`
    : 'No rate drops recorded';

  // ── Grouping for charts + tables ────────────────────────────────────────────
  type Group = { key: string; label: string; evs: RdEvent[]; fs: RdFiring[] };
  let groups: Group[] = [];
  if (level === 'pad') {
    const wells = new Map<number, string>();
    events.forEach(e => wells.set(e.well_id, e.well_name));
    firings.forEach(f => { if (f.well_id != null) wells.set(f.well_id, f.well_name ?? `Well ${f.well_id}`); });
    groups = Array.from(wells, ([id, name]) => ({
      key: `w${id}`, label: name, evs: events.filter(e => e.well_id === id), fs: firings.filter(f => f.well_id === id),
    })).sort((x, y) => x.label.localeCompare(y.label, undefined, { numeric: true }));
    const unplaced = firings.filter(f => f.well_id == null);
    if (unplaced.length) groups.push({ key: 'none', label: 'No stage', evs: [], fs: unplaced });
  } else if (level === 'well') {
    const nums = new Set<number>();
    events.forEach(e => nums.add(e.stage_number));
    firings.forEach(f => { if (f.stage_number != null) nums.add(f.stage_number); });
    groups = Array.from(nums).sort((x, y) => x - y).map(n => ({
      key: `s${n}`, label: `S${n}`, evs: events.filter(e => e.stage_number === n), fs: firings.filter(f => f.stage_number === n),
    }));
  }
  const gAgg = groups.map(g => ({ ...g, a: agg(g.evs, g.fs) }));

  // ── Chart 1: commanded drops ────────────────────────────────────────────────
  const chart1: Highcharts.Options = level === 'stage'
    ? {
        xAxis: { categories: firings.map(f => fmtUtc(f.fired_at_ms).slice(6, 11)) },
        yAxis: { title: { text: 'bpm', style: { color: TEXT_SEC } } },
        series: [
          { type: 'column', name: 'Commanded drop', data: firings.map(f => ({ y: f.commanded_drop, color: STATUS_COLOR[f.status] })) },
          { type: 'column', name: 'SAFA actual',    data: firings.map(f => actualOf(f)), color: GOLD },
        ] as Highcharts.SeriesOptionsType[],
        tooltip: {
          formatter: function (this: any) {
            const f = firings[this.points?.[0]?.point?.index ?? 0];
            if (!f) return '';
            const act = actualOf(f);
            return `<b>${fmtUtc(f.fired_at_ms)} UTC</b><br/>${f.rule_name}<br/>`
              + `<span style="color:${STATUS_COLOR[f.status]}">●</span> ${f.status}${f.reason ? ` — ${f.reason}` : ''}<br/>`
              + `Commanded: ${f1(f.commanded_drop)} bpm<br/>SAFA actual: ${f1(act)}${act != null ? ' bpm' : ''}`
              + (act != null && f.commanded_drop != null ? `<br/>Δ ${sgn(act - f.commanded_drop)} bpm` : '');
          },
        },
      }
    : {
        xAxis: { categories: gAgg.map(g => g.label) },
        yAxis: { title: { text: 'firings', style: { color: TEXT_SEC } } },
        plotOptions: { column: { stacking: 'normal' } },
        series: STATUS_ORDER.map(s => ({
          type: 'column', name: s, color: STATUS_COLOR[s], data: gAgg.map(g => g.a.byStatus[s]),
        })) as Highcharts.SeriesOptionsType[],
      };

  // ── Chart 2: SAFA-detected drops ────────────────────────────────────────────
  const chart2: Highcharts.Options = level === 'stage'
    ? {
        xAxis: { categories: events.map(e => fmtUtc(e.event_time_ms).slice(6, 11)) },
        yAxis: { title: { text: 'rate change (bpm)', style: { color: TEXT_SEC } } },
        legend: { enabled: false },
        series: [{
          type: 'column', name: 'Rate change',
          data: events.map(e => e.processable
            ? { y: e.rate_change ?? 0, color: GREEN }
            : { y: 0.3, color: RED }),
        }] as Highcharts.SeriesOptionsType[],
        tooltip: {
          formatter: function (this: any) {
            const e = events[this.points?.[0]?.point?.index ?? 0];
            if (!e) return '';
            return `<b>${fmtUtc(e.event_time_ms)} UTC</b><br/>`
              + (e.processable
                  ? `<span style="color:${GREEN}">●</span> Processable · ${f1(e.rate_change)} bpm`
                  : `<span style="color:${RED}">●</span> Not analyzable<br/>${e.reasons.map(shortSafa).join('<br/>')}`);
          },
        },
      }
    : {
        xAxis: { categories: gAgg.map(g => g.label) },
        yAxis: { title: { text: 'SAFA events', style: { color: TEXT_SEC } } },
        plotOptions: { column: { stacking: 'normal' } },
        series: [
          { type: 'column', name: 'Processable',    color: GREEN, data: gAgg.map(g => g.a.ok) },
          { type: 'column', name: 'Not analyzable', color: RED,   data: gAgg.map(g => g.a.bad) },
        ] as Highcharts.SeriesOptionsType[],
      };

  // ── Table ───────────────────────────────────────────────────────────────────
  const summaryCols: Col[] = [
    { h: 'Cmd Drops' }, { h: 'Accepted' }, { h: 'Acc %' }, { h: 'Top Not-Accepted Reason', align: 'left' },
    { h: 'SAFA Events' }, { h: 'Proc %' }, { h: 'Avg Cmd' }, { h: 'Avg Actual' }, { h: 'Avg Δ' }, { h: 'Field Score' },
  ];
  const summaryCells = (x: Agg): CellV[] => [
    { t: String(x.fn) },
    { t: String(x.acc), c: GREEN },
    { t: pct(x.apct), c: pctColor(x.apct) },
    { t: x.petrix[0] ? `${x.petrix[0].name} (${x.petrix[0].count})` : 'None', c: TEXT_SEC },
    { t: String(x.n) },
    { t: pct(x.pct), c: pctColor(x.pct) },
    { t: f2(x.cmd), c: x.cmd == null ? TEXT_DIM : BLUE },
    { t: f2(x.act), c: x.act == null ? TEXT_DIM : GOLD },
    { t: sgn(x.delta), c: deltaColor(x.delta) },
    { t: x.fld ? x.fld.score.toFixed(1) : NA, c: scoreColor(x.fld?.score ?? null) },
  ];

  let tableTitle: string, cols: Col[], rows: CellV[][];
  if (level === 'stage') {
    tableTitle = 'Commanded Rate Drop Log';
    cols = [
      { h: 'Fired (UTC)', align: 'left' }, { h: 'Rule', align: 'left' }, { h: 'Rate Before' }, { h: 'Cmd Drop' },
      { h: 'Petrix Response', align: 'left' }, { h: 'Why Not Accepted', align: 'left' },
      { h: 'SAFA Result', align: 'left' }, { h: 'Actual' }, { h: 'Δ' },
    ];
    rows = firings.map(f => {
      const ev  = f.matched_event_id != null ? evById.get(f.matched_event_id) : undefined;
      const act = actualOf(f);
      const d   = act != null && f.commanded_drop != null ? act - f.commanded_drop : null;
      const safa: CellV =
        ev ? (ev.processable ? { t: 'Processable', c: GREEN } : { t: `Not analyzable: ${ev.reasons.map(shortSafa).join(' · ')}`, c: RED })
        : f.status === 'Accepted' ? { t: 'No SAFA event within 60 s', c: ORANGE }
        : { t: NA, c: TEXT_DIM };
      return [
        { t: fmtUtc(f.fired_at_ms), c: TEXT_SEC },
        { t: f.rule_name, c: TEXT_SEC },
        { t: f.rate_before != null ? `${f1(f.rate_before)} bpm` : NA },
        { t: f1(f.commanded_drop), c: BLUE },
        { t: f.status, c: STATUS_COLOR[f.status] },
        { t: f.reason ?? 'None', c: TEXT_SEC },
        safa,
        { t: f1(act), c: act == null ? TEXT_DIM : GOLD },
        { t: sgn(d), c: deltaColor(d) },
      ];
    });
  } else if (level === 'well') {
    tableTitle = 'Stage Summary';
    cols = [{ h: 'Stage', align: 'left' }, ...summaryCols];
    rows = gAgg.map(g => [{ t: g.label, c: GOLD }, ...summaryCells(g.a)]);
  } else {
    tableTitle = 'Well Summary';
    cols = [{ h: 'Well', align: 'left' }, { h: 'Stages' }, ...summaryCols];
    rows = gAgg.map(g => [
      { t: g.label, c: g.key === 'none' ? TEXT_SEC : GOLD },
      { t: g.key === 'none' ? NA : String(g.a.stages), c: g.key === 'none' ? TEXT_DIM : undefined },
      ...summaryCells(g.a),
    ]);
  }

  const foot = 'Firings are placed on a stage by time. Matched = accepted firing with a SAFA event ≤60 s later. '
    + 'Δ = SAFA rate_change − commanded drop; N/A where either is missing.'
    + (level === 'pad' && groups.some(g => g.key === 'none') ? ' "No stage" = fired outside every stage window.' : '');

  // ── Excel ───────────────────────────────────────────────────────────────────
  const fileBase =
    level === 'stage' ? `rate_drops_stage${context.stage_number}_${context.well_name ?? ''}` :
    level === 'well'  ? `rate_drops_well_${context.well_name ?? entityId}` :
    `rate_drops_pad_${context.pad_name ?? entityId}`;

  const exportExcel = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx').catch(() => null);
      if (!XLSX) { alert('Run: npm install xlsx'); return; }
      const wb  = XLSX.utils.book_new();
      const iso = (ms: number) => new Date(ms).toISOString();
      const firingRows = [
        ['Execution ID', 'Fired (UTC)', 'Well', 'Stage', 'Rule', 'Rate Before', 'Rate Commanded', 'Commanded Drop',
         'Status', 'Why Not Accepted', 'Petrix Message', 'Matched SAFA Event ID', 'SAFA Status', 'SAFA Rate Change', 'Δ (actual − cmd)'],
        ...firings.map(f => {
          const ev  = f.matched_event_id != null ? evById.get(f.matched_event_id) : undefined;
          const act = actualOf(f);
          return [
            f.id, iso(f.fired_at_ms), f.well_name, f.stage_number != null ? `S${f.stage_number}` : null, f.rule_name,
            f.rate_before, f.rate_commanded, f.commanded_drop, f.status, f.reason, f.mqtt_error,
            f.matched_event_id, ev?.processing_status ?? null, act,
            act != null && f.commanded_drop != null ? Number((act - f.commanded_drop).toFixed(2)) : null,
          ];
        }),
      ];
      const eventRows = [
        ['Event ID', 'Event Time (UTC)', 'Well', 'Stage', 'Status', 'Not-Analyzable Reasons', 'Initial Rate', 'Rate Change', 'Perf Eff %'],
        ...events.map(e => [
          e.id, iso(e.event_time_ms), e.well_name, `S${e.stage_number}`, e.processing_status,
          e.reasons.join('; ') || null, e.initial_rate, e.rate_change,
          e.eff != null ? Number((e.eff * 100).toFixed(1)) : null,
        ]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(firingRows), 'Commanded Drops');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(eventRows),  'SAFA Events');
      XLSX.writeFile(wb, `${fileBase}.xlsx`);
    } catch (e: any) {
      alert(`Excel export failed: ${e.message}`);
    } finally { setExporting(false); }
  };

  const exportPdf = async () => {
    if (!ref.current) return;
    setExporting(true);
    try { await exportScorecardPdf(ref.current, fileBase); }
    catch (e: any) { alert(`PDF export failed: ${e.message}`); }
    finally { setExporting(false); }
  };

  const btn = (color: string): React.CSSProperties => ({
    background: 'transparent', border: `1px solid ${color}`, color, borderRadius: 4, padding: '4px 12px',
    fontSize: 11, fontWeight: 600, cursor: exporting ? 'wait' : 'pointer', opacity: exporting ? 0.6 : 1, fontFamily: 'inherit',
  });

  const pts = (p: number) => `${(33.3 * p).toFixed(1)} of 33.3 pts`;
  const isStage = level === 'stage';

  return (
    <div ref={ref} style={rootStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
                    borderBottom: `1px solid ${BORDER}`, paddingBottom: 14, marginBottom: 16 }}>
        <div style={{ width: 150 }} />
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 11, color: TEXT_SEC, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>Rate Drop Scorecard</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: TEXT_PRI }}>{title}</div>
          <div style={{ fontSize: 11, color: TEXT_SEC, marginTop: 4 }}>{sub}</div>
        </div>
        <div data-pdf-hide style={{ width: 150, display: 'flex', gap: 6, justifyContent: 'flex-end', paddingTop: 4 }}>
          <button onClick={exportExcel} disabled={exporting} style={btn(GREEN)}>{exporting ? '…' : '⬇'} Excel</button>
          <button onClick={exportPdf}   disabled={exporting} style={btn(RED)}>{exporting ? '…' : '⬇'} PDF</button>
        </div>
      </div>

      {/* Field metrics */}
      <SectionLabel right={fd ? 'Per stage · "successful" = SAFA processable · each metric worth 33.3' : 'No SAFA events in this scope'}>
        Field Metrics · SAFA Quality
      </SectionLabel>
      {fd && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10, marginBottom: 16 }}>
          <FieldCard label="1st rate drop — completed successfully"
                     value={isStage ? (fd.first ? 'Yes' : 'No') : pct(fd.f)} color={pctColor(fd.f)}
                     detail={isStage ? 'first SAFA event processable' : `${fd.first} of ${fd.n} stages`}
                     frac={fd.f} weight={pts(fd.f)} />
          <FieldCard label="Successful drops vs active scheduled drops"
                     value={pct(fd.r)} color={pctColor(fd.r)} detail={`${fd.ok} of ${fd.tot} drops`}
                     frac={fd.r} weight={pts(fd.r)} />
          <FieldCard label="Last rate drop — completed successfully"
                     value={isStage ? (fd.last ? 'Yes' : 'No') : pct(fd.l)} color={pctColor(fd.l)}
                     detail={isStage ? 'last SAFA event processable' : `${fd.last} of ${fd.n} stages`}
                     frac={fd.l} weight={pts(fd.l)} />
          <FieldCard label="Field score" highlight
                     value={fd.score.toFixed(1)} color={scoreColor(fd.score)} detail="out of 99.9"
                     frac={fd.score / 99.9}
                     weight={`${(33.3 * fd.f).toFixed(1)} + ${(33.3 * fd.r).toFixed(1)} + ${(33.3 * fd.l).toFixed(1)}`} />
        </div>
      )}

      {/* Machina execution */}
      <SectionLabel>Machina Execution</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 8, marginBottom: 16 }}>
        <Card label="Commanded Drops" value={String(a.fn)} note="Machina rule firings" />
        <Card label="Accepted" value={a.fn ? `${a.acc} · ${pct(a.apct)}` : NA} color={a.fn ? GREEN : TEXT_DIM}
              note={a.noReason ? `${a.noReason} rejections had no reason` : 'of commanded'} />
        <Card label="Top Not-Accepted Reason" small
              value={a.petrix[0]?.name ?? 'None'} color={a.petrix[0] ? RED : TEXT_DIM}
              note={a.petrix[0] ? `${a.petrix[0].count} · ${pct(a.petrix[0].count / Math.max(a.nacc, 1))} of not accepted` : undefined} />
        <Card label="SAFA Confirmed" value={a.acc ? `${a.conf} of ${a.acc}` : NA} color={a.acc ? pctColor(a.cpct) : TEXT_DIM}
              note={a.acc ? `${pct(a.cpct)} of accepted · SAFA event ≤60 s` : 'no accepted drops'} />
        <Card label="Commanded → Actual" value={a.cmd == null ? NA : `${f2(a.cmd)} → ${f2(a.act)}`}
              color={a.cmd == null ? TEXT_DIM : TEXT_PRI} note="avg bpm · commanded vs SAFA" />
        <Card label="Avg Δ Rate" value={a.delta == null ? NA : `${sgn(a.delta)} bpm`} color={deltaColor(a.delta)}
              note={`${a.pairs} matched drops · actual − cmd`} />
      </div>

      {/* Commanded drops */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 14, marginBottom: 14 }}>
        <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 6, padding: '12px 14px', minWidth: 0 }}>
          <SectionLabel>{isStage ? 'Each commanded rate drop — commanded vs SAFA actual' : `Commanded rate drops — by ${level === 'pad' ? 'well' : 'stage'}`}</SectionLabel>
          {firings.length ? <Chart options={chart1} /> : <div style={{ color: TEXT_DIM, fontSize: 11, padding: '20px 0' }}>No rule firings in this scope.</div>}
        </div>
        <ReasonBars title="Why commanded drops weren't accepted" sub={`${a.nacc} of ${a.fn} firings · Petrix response`}
                    items={a.petrix} total={a.nacc} legend={PETRIX_CATS} />
      </div>

      {/* SAFA-detected drops */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 14 }}>
        <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 6, padding: '12px 14px', minWidth: 0 }}>
          <SectionLabel>{isStage ? 'Each SAFA-detected rate drop — rate change' : `SAFA-detected rate drops — by ${level === 'pad' ? 'well' : 'stage'}`}</SectionLabel>
          {events.length ? <Chart options={chart2} /> : <div style={{ color: TEXT_DIM, fontSize: 11, padding: '20px 0' }}>No SAFA events in this scope.</div>}
        </div>
        <ReasonBars title="Why SAFA couldn't analyze the drop" sub={`${a.bad} events · multiple reasons each`}
                    items={a.safa} total={a.bad} legend={SAFA_CATS.map(c => ({ name: c.name, color: c.color }))} />
      </div>

      <Table title={tableTitle} cols={cols} rows={rows} foot={foot} />
    </div>
  );
};

const rdThStyle: React.CSSProperties = {
  padding: '7px 10px', color: TEXT_SEC, fontSize: 10, fontWeight: 600, letterSpacing: '0.07em',
  textTransform: 'uppercase', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap',
};
const rdTdStyle: React.CSSProperties = { padding: '7px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

// ─── Events view (perf-eff scorecard) ─────────────────────────────────────────
const EventsView: React.FC<Omit<Props, 'view'>> = ({ level, entityId, apiBase, token, isEditor = false }) => {
  const [data,    setData]    = useState<ScorecardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [metrics, setMetrics] = useState<MetricToggle>({
    eff: true, perf_frict: true, pipe_frict: true, ehd: true, regression: true,
  });
  const toggle = (k: keyof MetricToggle) => setMetrics(m => ({ ...m, [k]: !m[k] }));
  const scorecardRef = React.useRef<HTMLDivElement>(null);

  // ── Main scorecard data ───────────────────────────────────────────────────
  // silent = true reloads in place (after a Seismos refresh) without the
  // full-card "Loading…" swap, so the chart and refresh status stay on screen.
  const loadScorecard = useCallback((silent: boolean) => {
    if (!entityId) return;
    if (!silent) setLoading(true);
    setError(null);
    fetch(`${apiBase}/seismos/scorecard/${level}/${entityId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [level, entityId, apiBase, token]);

  useEffect(() => { loadScorecard(false); }, [loadScorecard]);

  // ── Seismos interventions (manually logged — well/pad level only) ─────────
  const [seismosInterventions, setSeismosInterventions] = useState<{ stage_id: number }[]>([]);
  const fetchSeismosInterventions = useCallback(async () => {
    if (level === 'stage' || !entityId) return;
    const param = level === 'well' ? `well_id=${entityId}` : `pad_id=${entityId}`;
    try {
      const res = await fetch(`${apiBase}/api/seismos-interventions?${param}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSeismosInterventions(await res.json());
    } catch { /* silently ignore — scorecard works without it */ }
  }, [level, entityId, apiBase, token]);

  useEffect(() => { fetchSeismosInterventions(); }, [fetchSeismosInterventions]);

  const respondedStageIds = useMemo(
    () => new Set(seismosInterventions.map(si => si.stage_id)),
    [seismosInterventions],
  );

  // eff is stored as 0–1 in seismos_metrics; multiply × 100 for all display + chart logic
  const normalized = useMemo(() => {
    if (!data) return null;
    return {
      ...data,
      events: data.events?.map(e => ({
        ...e,
        eff: e.eff != null ? Math.round(e.eff * 1000) / 10 : null, // × 100, 1 dp
      })) ?? null,
      stages: data.stages?.map(s => ({
        ...s,
        avg_eff: s.avg_eff != null ? Math.round(s.avg_eff * 1000) / 10 : null,
      })) ?? null,
    };
  }, [data]);

  // ── Regression (shared logic, adapts to level) ───────────────────────────
  const { regressionStage, regressionAggregate, slopeLabel } = useMemo(() => {
    if (!normalized) return { regressionStage: null, regressionAggregate: null, slopeLabel: null };

    if (level === 'stage' && normalized.events) {
      const pts = normalized.events.filter(e => e.eff != null).map(e => ({ x: e.event_time_ms, y: e.eff! }));
      const reg = linearRegression(pts);
      if (!reg || pts.length < 2) return { regressionStage: null, regressionAggregate: null, slopeLabel: null };
      const xMin = pts[0].x, xMax = pts[pts.length - 1].x;
      const regLine = [
        { event_time_ms: xMin, regression: reg.intercept + reg.slope * xMin },
        { event_time_ms: xMax, regression: reg.intercept + reg.slope * xMax },
      ];
      const dy = regLine[1].regression - regLine[0].regression;
      const dx = (xMax - xMin) / 60000;
      const spm = dx ? dy / dx : 0;
      return {
        regressionStage: regLine,
        regressionAggregate: null,
        slopeLabel: { label: `${spm > 0 ? '+' : ''}${spm.toFixed(2)}% / min`, positive: spm > 0 },
      };
    }

    if (normalized.stages) {
      const chartStages = normalized.stages.map((s, i) => ({ ...s, seqIndex: i + 1 }));
      const pts = chartStages.filter(s => s.avg_eff != null).map(s => ({ x: s.seqIndex, y: s.avg_eff! }));
      const reg = linearRegression(pts);
      if (!reg || pts.length < 2) return { regressionStage: null, regressionAggregate: null, slopeLabel: null };
      const xMin = 1, xMax = chartStages.length;
      const regLine = [
        { seqIndex: xMin, regression: reg.intercept + reg.slope * xMin },
        { seqIndex: xMax, regression: reg.intercept + reg.slope * xMax },
      ];
      const dy = regLine[1].regression - regLine[0].regression;
      return {
        regressionStage: null,
        regressionAggregate: regLine,
        slopeLabel: { label: `${dy > 0 ? '+' : ''}${dy.toFixed(1)}% over ${xMax} stages`, positive: dy > 0 },
      };
    }

    return { regressionStage: null, regressionAggregate: null, slopeLabel: null };
  }, [normalized, level]);

  // All stages — no rule filtering needed
  const filteredStages = useMemo(() => normalized?.stages ?? null, [normalized]);

  // ─── Render ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...rootStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
      <div style={{ color: TEXT_SEC, fontSize: 13 }}>Loading…</div>
    </div>
  );
  if (error) return (
    <div style={{ ...rootStyle, color: '#e05c5c', fontSize: 13 }}>Error: {error}</div>
  );
  if (!normalized) return null;

  const { context, events, stages } = normalized;
  const showWell = level === 'pad';
  const noChart = level === 'stage' ? !events?.length : !stages?.length;

  return (
    <div ref={scorecardRef} style={rootStyle}>
      {/* Header row with export buttons */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <ScorecardHeader level={level} context={context} />
        </div>
        <div style={{ paddingTop: 4, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <ExportButtons
            level={level} entityId={entityId} context={context}
            events={events} stages={stages} apiBase={apiBase} token={token}
            scorecardRef={scorecardRef}
          />
        </div>
      </div>

      <StatCards level={level} events={events} stages={stages} slopeLabel={slopeLabel} uniformityIndex={context.uniformity_index} />

      {/* Metric toggles */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, justifyContent: 'center' }}>
        <Pill label="Perf Eff %"    color={C_EFF}     active={metrics.eff}        onClick={() => toggle('eff')} />
        <Pill label="Perf Friction" color={C_PERF_FR} active={metrics.perf_frict} onClick={() => toggle('perf_frict')} />
        <Pill label="Pipe Friction" color={C_PIPE_FR} active={metrics.pipe_frict} onClick={() => toggle('pipe_frict')} />
        <Pill label="EHD"           color={C_EHD}     active={metrics.ehd}        onClick={() => toggle('ehd')} />
        <Pill label="Trend Line"    color={C_REGRESS} active={metrics.regression} onClick={() => toggle('regression')} />
      </div>

      {/* Chart */}
      {noChart ? (
        <div style={{ color: TEXT_SEC, fontSize: 13, padding: '24px 0' }}>No Seismos data for this {level}.</div>
      ) : level === 'stage' ? (
        <StageChart
          events={events!}
          metrics={metrics}
          regressionData={regressionStage}
          slopeLabel={slopeLabel}
        />
      ) : (
        <AggregateChart
          level={level as 'well' | 'pad'}
          stages={stages!}
          metrics={metrics}
          regressionData={regressionAggregate}
          slopeLabel={slopeLabel}
        />
      )}

      {/* Seismos Comparison Chart — well/pad: responded vs baseline trend */}
      {level !== 'stage' && stages && (
        <SeismosComparisonChart
          stages={stages}
          respondedStageIds={respondedStageIds}
          showWell={showWell}
          seismosInterventions={seismosInterventions}
          apiBase={apiBase}
          token={token}
          level={level as 'well' | 'pad'}
          entityId={entityId}
        />
      )}

      {/* Stage summary table */}
      {level !== 'stage' && filteredStages?.length ? (
        <StageTable stages={filteredStages} showWell={showWell} apiBase={apiBase} token={token} />
      ) : null}


      {/* Event Log with inline Seismos tagging — stage level only */}
      {level === 'stage' && events?.length && context.stage_id != null && (
        <SeismosEventLog
          events={events}
          stageId={context.stage_id}
          isEditor={isEditor}
          apiBase={apiBase}
          token={token}
        />
      )}
    </div>
  );
};

// ─── Shared style objects ──────────────────────────────────────────────────────
const rootStyle: React.CSSProperties = {
  background: BG, border: `1px solid ${BORDER}`, borderRadius: 8,
  padding: '18px 22px',
  fontFamily: "'DM Mono', 'Fira Code', 'Courier New', monospace",
  color: TEXT_PRI,
};
const sectionLabel: React.CSSProperties = {
  fontSize: 11, color: TEXT_SEC, letterSpacing: '0.08em',
  textTransform: 'uppercase', marginBottom: 8,
};
const thStyle: React.CSSProperties = {
  padding: '7px 12px', textAlign: 'right', color: TEXT_SEC, fontSize: 10,
  fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase',
  borderBottom: `1px solid ${BORDER}`,
};
const tdStyle: React.CSSProperties = { padding: '8px 12px', color: TEXT_PRI };

// ─── Main Component ────────────────────────────────────────────────────────────
// One component, two views. The modal's Events / Rate Drops toggle sets `view`.
const SeismosScorecard: React.FC<Props> = ({ view = 'events', ...rest }) =>
  view === 'rates' ? <RateDropView {...rest} /> : <EventsView {...rest} />;

export default SeismosScorecard;